import { createAxiosInstance } from "api/axiosConfig";
import { PENDING_IB_REFERRAL_KEY } from "features/referrals/constants";

export const IB_RETURN_MAX_ATTEMPTS = 3;
export const IB_RETURN_RETRY_DELAY_MS = 2000;

// The markers that signal a pending return from the Healthlink Integrated
// Browser, in the order we trust them:
//   1. sessionStorage — stashed before the auto-redirect; the reliable source,
//      as HealthLink drops our redirectUrl params on the way back.
//   2. ?referralId — legacy links that still carry it.
//   3. ?controlId — HealthLink's own message control number.
export const readIbReturnMarkers = (searchParams) => ({
  pendingReferralId: sessionStorage.getItem(PENDING_IB_REFERRAL_KEY),
  referralIdParam: searchParams.get("referralId"),
  controlIdParam: searchParams.get("controlId"),
});

export const hasPendingIbReturn = ({
  pendingReferralId,
  referralIdParam,
  controlIdParam,
}) => !!pendingReferralId || !!referralIdParam || !!controlIdParam;

// Resolve the internal referral id from the markers. The controlId is mapped to
// our internal id via the patient's referral list (newest first, so a just-
// created referral is on the first page).
export const resolveIbReferralId = async ({
  pendingReferralId,
  referralIdParam,
  controlIdParam,
  patientId,
  tenantVal,
}) => {
  if (pendingReferralId) return pendingReferralId;
  if (referralIdParam) return referralIdParam;
  if (!patientId || !tenantVal) return null;

  try {
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.get(
      `${tenantVal}/healthlink/patients/${patientId}/referrals?page=1&rows=50&orderBy=DESC`
    );
    const match = (response?.data?.data ?? []).find(
      (item) => item.messageControlId === controlIdParam
    );

    return match?.id ?? null;
  } catch {
    return null;
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Poll check-status until the referral is confirmed or attempts are exhausted.
export const pollReferralConfirmation = async (
  checkReferralStatus,
  referralId
) => {
  for (let attempt = 1; attempt <= IB_RETURN_MAX_ATTEMPTS; attempt++) {
    try {
      const data = await checkReferralStatus(referralId);

      if (data.confirmed) return true;
    } catch {
      // network error — fall through to retry
    }

    if (attempt < IB_RETURN_MAX_ATTEMPTS) {
      await wait(IB_RETURN_RETRY_DELAY_MS);
    }
  }

  return false;
};
